import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AsYouType, CountryCode, parsePhoneNumber } from 'libphonenumber-js';

import { digitRegex, regexpMatchLength } from '@blaze-shared/shared-util';

import { BaseCellInputComponent } from '../base-cell-input.component';

@UntilDestroy()
@Component({
  selector: 'blaze-cell-input-phone',
  template: `<input
    #phoneInput
    type="text"
    [formControl]="phoneInputDisplayValue"
    [class.readonly]="column.isComputed"
    [attr.readonly]="column.isComputed ? true : null"
    class="input-string"
  />`,
  styleUrls: ['../base-cell-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule],
})
export class CellInputPhoneComponent extends BaseCellInputComponent<string> implements OnInit {
  @ViewChild('phoneInput') phoneInputRef!: ElementRef<HTMLInputElement>;

  phoneInputDisplayValue: FormControl = new FormControl();

  override get nativeElement(): HTMLInputElement | null {
    return this.phoneInputRef?.nativeElement || null;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.phoneInputDisplayValue = this.createPhoneNumberFormControl();
  }

  private getValueForPhoneInput(value: typeof this.value): string {
    if (!(typeof value === 'string' || value == null)) {
      return '';
    }
    return this.getPhoneNumber(value ?? undefined).asYouTypeFormatter ?? '';
  }

  private getValueToEmit(value: string | null): string {
    return this.getPhoneNumber(value ?? undefined).numberValue;
  }

  private createPhoneNumberFormControl(): FormControl {
    const formControl = new FormControl({
      value: this.getValueForPhoneInput(this.value),
      disabled: this.isDisabled,
    });

    formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      const newValue = this.getValueToEmit(value);
      this.formControl.setValue(newValue, { emitEvent: true });

      const asYouType = this.getPhoneNumber(value ?? undefined).asYouTypeFormatter;
      if (value !== asYouType) {
        this.phoneInputDisplayValue.setValue(asYouType, { emitEvent: false });
      }
    });

    return formControl;
  }

  private getPhoneNumber(inputValue = ''): { asYouTypeFormatter?: string; numberValue: string } {
    if (this.column.format?.formatType !== 'PhoneNumber') {
      return { numberValue: inputValue };
    }

    const savedCountry = this.column.format?.culture?.split('-')[1];
    const country = (savedCountry as CountryCode) || 'US';

    const asYouTypeFormatter =
      regexpMatchLength(digitRegex, inputValue) > 3
        ? new AsYouType(country).input(inputValue || '')
        : inputValue;
    const numberValue =
      regexpMatchLength(digitRegex, inputValue) > 3
        ? parsePhoneNumber(inputValue, country).number
        : inputValue;

    return {
      asYouTypeFormatter,
      numberValue,
    };
  }
}
